import type { FunctionComponent, PropsWithChildren } from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDebounceCallback } from 'usehooks-ts';

import { useApplicationContext } from '~/shared/context/use-application-context';
import { useTranslate } from '~/shared/context/use-translate';
import { getTableZoom, TABLE_ZOOM_DEFAULT } from '~/shared/data-contract/user-ui-settings';
import {
    clampTableZoom,
    type TableZoomLabels,
    TableZoomProvider,
    type TableZoomUpdate,
} from '~/ui/core/table/TableZoomContext';

import { useUserSettingsUiMutation } from './query-hooks/use-user-settings-ui';

// Wheel and pinch fire continuously; persist once the gesture settles instead of on every notch.
const PERSIST_DEBOUNCE_MS = 400;

// One zoom level for every shared table, persisted with the user's UI settings.
export const TableZoomPolicyProvider: FunctionComponent<PropsWithChildren> = ({ children }) => {
    const {
        applicationState: { uiSettings },
        validatingCobrainerApiFetcher,
    } = useApplicationContext();
    const { userSettingsUiMutationOptimistic } = useUserSettingsUiMutation(validatingCobrainerApiFetcher);
    const { mutate } = userSettingsUiMutationOptimistic;
    const translate = useTranslate();
    const labels = useMemo<TableZoomLabels>(
        () => ({
            group: translate('components.table.zoom'),
            reset: translate('components.table.zoom-reset'),
            zoomIn: translate('components.table.zoom-in'),
            zoomOut: translate('components.table.zoom-out'),
        }),
        [translate],
    );
    const persistedZoom = uiSettings.mapOr(TABLE_ZOOM_DEFAULT, getTableZoom);
    const [zoom, setZoomState] = useState(persistedZoom);
    // Local edits own the zoom level from the moment the user changes it until every request that
    // change triggered has settled. Only then may the settings query drive the table again, so a late
    // response, a rollback, or a revalidation during the debounce cannot reset a newer level.
    const pendingRequests = useRef(0);
    const hasUnsentEdit = useRef(false);

    useEffect(() => {
        if (pendingRequests.current === 0 && !hasUnsentEdit.current) {
            setZoomState(persistedZoom);
        }
    }, [persistedZoom]);

    const persist = useCallback(
        (nextZoom: number): void => {
            hasUnsentEdit.current = false;
            pendingRequests.current += 1;
            mutate(
                { tableZoom: nextZoom },
                {
                    onSettled: () => {
                        pendingRequests.current -= 1;
                    },
                },
            );
        },
        [mutate],
    );
    const persistZoom = useDebounceCallback(persist, PERSIST_DEBOUNCE_MS);

    const setZoom = useCallback(
        (update: TableZoomUpdate): void => {
            hasUnsentEdit.current = true;
            setZoomState((current) => {
                // Three decimals so a capped wheel notch (about 0.8%) is never rounded away to a no-op.
                const next =
                    Math.round(clampTableZoom(typeof update === 'function' ? update(current) : update) * 1000) / 1000;
                persistZoom(next);

                return next;
            });
        },
        [persistZoom],
    );

    return (
        <TableZoomProvider labels={labels} zoom={zoom} onZoomChange={setZoom}>
            {children}
        </TableZoomProvider>
    );
};
